import type { RowHeights } from '../types/dashboard-config.js';

// Pure geometry for a page builder cell (a block's items, or a section's widgets): entries sit in
// rows and side-by-side slots, and an entry may cover several rows (`rowSpan`) so that other entries
// can stack in the rows beside it. Shared by the builder and its tests; no React or DB dependencies.

/** Anything placed in a cell. Block widgets, sections and section widgets all fit this. */
export type CellBox = { row?: number; slot: number; span: number; rowSpan?: number };

export const MAX_ROW_SPAN = 12;
/** Smallest height a row can be pinned to by dragging. */
export const MIN_PINNED_ROW_HEIGHT = 48;

const rowOf = (e: CellBox): number => e.row ?? 1;
const rowCount = (e: CellBox): number => Math.max(1, e.rowSpan ?? 1);
const colsOverlap = (a: CellBox, b: CellBox): boolean => a.slot < b.slot + b.span && b.slot < a.slot + a.span;

/** Whether two entries share a grid cell: overlapping rows and overlapping slots. */
export function boxesOverlap(a: CellBox, b: CellBox): boolean {
  return rowOf(a) < rowOf(b) + rowCount(b) && rowOf(b) < rowOf(a) + rowCount(a) && colsOverlap(a, b);
}

/**
 * Places `moving` at (toRow, toSlot) and moves every other entry its footprint overlaps (in any row it
 * covers) out of the way, below everything. Entries that sat side by side in a row still do. Returns
 * the others followed by the placed entry. `moving` must not be in `others`.
 */
export function placeEntry<T extends CellBox>(others: T[], moving: T, toRow: number, toSlot: number, cols: number): T[] {
  const placed: T = { ...moving, row: toRow, slot: toSlot, span: Math.max(1, Math.min(moving.span, cols + 1 - toSlot)) };
  const displaced = others.filter((o) => boxesOverlap(o, placed));
  if (displaced.length === 0) return [...others, placed];

  const bottom = Math.max(toRow + rowCount(placed), ...others.map((o) => rowOf(o) + rowCount(o)));
  const landing = new Map<number, number>();
  let cursor = bottom;
  for (const row of [...new Set(displaced.map(rowOf))].sort((a, b) => a - b)) {
    landing.set(row, cursor);
    cursor += Math.max(...displaced.filter((d) => rowOf(d) === row).map(rowCount));
  }
  return [...others.map((o) => (displaced.includes(o) ? { ...o, row: landing.get(rowOf(o)) ?? bottom } : o)), placed];
}

/** How many stacked rows of at least `unit` px (with `gap` between them) fit in `px`, from 1 to `max`. */
export function rowsForHeight(px: number, unit: number, gap: number, max: number = MAX_ROW_SPAN): number {
  return Math.max(1, Math.min(max, Math.floor((px + gap) / (unit + gap))));
}

/** Renumbers rows to 1..n (rows an entry covers count as rows), remapping pinned heights to match. */
function compactRows<T extends CellBox>(
  entries: T[],
  rowHeights: RowHeights | undefined,
): { entries: T[]; rowHeights: RowHeights | undefined; rowTotal: number } {
  const occupied = new Set<number>();
  for (const e of entries) for (let r = rowOf(e); r < rowOf(e) + rowCount(e); r++) occupied.add(r);
  const sorted = [...occupied].sort((a, b) => a - b);
  const index = new Map(sorted.map((r, i) => [r, i + 1]));
  const heights: RowHeights = {};
  for (const [key, px] of Object.entries(rowHeights ?? {})) {
    const next = index.get(Number(key));
    if (next !== undefined) heights[String(next)] = px;
  }
  return {
    entries: entries.map((e) => ({ ...e, row: index.get(rowOf(e)) ?? 1 })),
    rowHeights: Object.keys(heights).length > 0 ? heights : undefined,
    rowTotal: sorted.length,
  };
}

function withRowSpan<T extends CellBox>(entry: T, span: number): T {
  const { rowSpan: _old, ...rest } = entry;
  return (span > 1 ? { ...rest, rowSpan: span } : rest) as T;
}

export type ResizeHeightInput<T extends CellBox> = {
  entries: T[];
  rowHeights: RowHeights | undefined;
  /** The widget being resized. */
  row: number;
  slot: number;
  /** The height dragged to, in px; undefined fits the widget to its content again. */
  height: number | undefined;
  /** Rendered height of each row of the cell, top to bottom, when the drag started. */
  tracks: number[];
  /** Gap between rows, in px. */
  gap: number;
  /** Smallest row, in px, when a tall widget makes the cell gain rows. */
  unit: number;
};

const HEIGHT_TOLERANCE = 4;

/**
 * Resizes a widget to `height` (see the stacking behaviour in the page builder guide):
 * - extending into free rows beside a taller widget snaps to a row boundary, so it can take all the space;
 * - growing with nothing free below adds rows (two stack at 2 units tall, three at 3, ...), pinned equal;
 * - shrinking a multi-row widget snaps to a row boundary, then to one pinned row;
 * - a single row pins its height, as dragging a row always has.
 */
export function resizeWidgetHeight<T extends CellBox>(input: ResizeHeightInput<T>): { entries: T[]; rowHeights: RowHeights | undefined } {
  const { height, tracks, gap, unit } = input;
  const index = input.entries.findIndex((e) => rowOf(e) === input.row && e.slot === input.slot);
  if (index < 0) return { entries: input.entries, rowHeights: input.rowHeights };

  const compact = compactRows(input.entries, input.rowHeights);
  const target = compact.entries[index];
  const others = compact.entries.filter((_, i) => i !== index);
  const top = rowOf(target);
  const own = rowCount(target);
  const heights = { ...compact.rowHeights };
  const clear = (from: number, count: number) => {
    for (let r = from; r < from + count; r++) delete heights[String(r)];
  };
  const done = (entry: T, entries: T[] = others) => ({
    entries: [...entries, entry],
    rowHeights: Object.keys(heights).length > 0 ? heights : undefined,
  });

  if (height === undefined) {
    clear(top, own);
    return done(withRowSpan(target, 1));
  }

  // Heights of the rows from the widget's top down, and the edge of each row boundary.
  const below = tracks.slice(top - 1);
  const edge = (rows: number) => below.slice(0, rows).reduce((sum, h) => sum + h, 0) + (rows - 1) * gap;
  const currentHeight = edge(own);

  // Rows directly under the widget with nothing in its columns: it can extend into them without moving anything.
  let free = 0;
  for (let r = top + own; r <= compact.rowTotal; r++) {
    if (others.some((o) => rowOf(o) <= r && r < rowOf(o) + rowCount(o) && colsOverlap(o, target))) break;
    free += 1;
  }

  const pinRows = (count: number, total: number) => {
    const each = Math.max(MIN_PINNED_ROW_HEIGHT, Math.floor((total - (count - 1) * gap) / count));
    for (let r = top; r < top + count; r++) heights[String(r)] = each;
  };
  const nearestRows = (from: number, to: number) => {
    let best = from;
    for (let k = from; k <= to; k++) if (Math.abs(edge(k) - height) < Math.abs(edge(best) - height)) best = k;
    return best;
  };

  if (Math.abs(height - currentHeight) <= HEIGHT_TOLERANCE) return done(target);

  if (height > currentHeight) {
    // Beside a taller widget: take as many of the free rows as the drag reaches, but never more. To go
    // past them the taller widget is the one to drag (re-pinning rows here would shrink it).
    if (free > 0) return done(withRowSpan(target, nearestRows(own, own + free)));
    const n = rowsForHeight(height, unit, gap);
    if (n < 2) {
      clear(top, own);
      heights[String(top)] = Math.max(MIN_PINNED_ROW_HEIGHT, Math.round(height));
      return done(withRowSpan(target, 1));
    }
    const grown = withRowSpan(target, n);
    pinRows(n, height);
    return { entries: placeEntry(others, grown, top, target.slot, Number.POSITIVE_INFINITY), rowHeights: Object.keys(heights).length > 0 ? heights : undefined };
  }

  if (own >= 2 && height >= edge(1)) return done(withRowSpan(target, nearestRows(1, own)));
  clear(top, own);
  heights[String(top)] = Math.max(MIN_PINNED_ROW_HEIGHT, Math.round(height));
  return done(withRowSpan(target, 1));
}
